import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { parseJson } from '../../../utils/parseJson';
import * as DataCells from '../../UI/DataCells/DataCells';
import { removeTagHtml } from '../../Utils/Utils';

import { lighten } from '../../Utils/colorManipulator';
import { DivCellText } from '../../UI/DataCells/DataCellsCSS';
import { CellList } from './CellList';
import ObjectActionCreators from '../../../actions';
import ObjFieldDate from '../../UI/Objects/ObjFields/ObjFieldDate';
import APortal from '../../UI/APortal/APortal';
import ObjFieldList from '../../UI/Objects/ObjFields/ObjFieldList';
import ObjFieldListMulti from '../../UI/Objects/ObjFields/objFieldListMulti';
import DataColumnResizer from './DataColumResizer';
import TiptapEditor from '../../UI/Objects/ObjFields/TiptapEditor';
import ObjectSelector from '../../object-selector';
import {
  CellListInlineGroup,
  CellListBadge,
  CellListBadgeIcon,
  CellListBadgeText,
} from './CellListCSS';
import { useFieldDef } from '../../../hooks/useFieldDef';
import { useFieldListResolver } from '../../../hooks/useFieldListResolver';
import { useFieldValueResolver } from '../../../hooks/useFieldValueResolver';
import { selectDisplayModel } from '../../../reducers/layoutReducer/displayModel';

export default function DataCellByType({
  item,
  node,
  id,
  panelNumber,
  colNumber,
  isLastCell = false,
  width,
  typeTo,
  name,
}) {
  let objTypeId = node.data.type;
  let fieldName = item.data;
  let data = node.data[fieldName];

  const fieldDef = useFieldDef({
    objectType: objTypeId,
    fieldName: fieldName,
    node: node,
    value: node.data[fieldName],
  });

  const fieldValue = useFieldValueResolver({ fieldDef, node });
  const singleFieldList = useFieldListResolver({
    fieldDef: fieldDef,
    singleValue:
      fieldDef.type === 'list' && !fieldDef.isMultiple
        ? fieldValue.rawValue
        : null,
  });

  const multiFieldList = useFieldListResolver({
    fieldDef: fieldDef,
    values:
      fieldDef.type === 'list' && fieldDef.isMultiple
        ? fieldValue.multiValues
        : [],
  });

  const cellInnerRef = React.useRef(null);
  // const containerW = useContainerWidth(cellInnerRef);
  const MULTI_CHIP_MIN = 100;
  const CHIP_GAP = 6;

  let level = node.depth - 1;
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(data);
  const dispatch = useDispatch();

  const { calculatedDisplayMode, fusedDataColumns } =
    useSelector(selectDisplayModel);
  const calculatedDisplayModes = calculatedDisplayMode;
  const dataColumns = fusedDataColumns;

  const isStacked = calculatedDisplayModes[level] === 'stack';

  const handleBlur = (e) => {
    setValue(e.target.value);
    setIsEditing(false);
  };

  const handleChangeDate = (name, date) => {
    setValue(date);
    setIsEditing(false);
  };
  const changeValue = (newValue) => {
    setIsEditing(false);
  };

  const handlePreventInputEvent = (e) => {
    e.stopPropagation();
    // e.preventDefault(); remving default behavior would impact +/- ui on input field
  };

  const debouncedSave = debounce((objFormData) => {
    dispatch(
      ObjectActionCreators.saveObjectFormData(node.data.key, [objFormData])
    );
  }, 500);

  useEffect(() => {
    if (value !== data) {
      let objFormData = {
        type: item.type,
        key: node.data.key,
        attrName: fieldDef.isMultiple ? `${item.data}_multval` : item.data,
        value: value,
        defaultValue: value,
      };
      if (fieldDef.canWrite) {
        debouncedSave(objFormData);
      }
    }
  }, [value]);

  const renderDateCell = (data, id) => {
    let value = '';
    try {
      const aDate = new Date(parseInt(data));
      if (!isNaN(aDate)) value = aDate.toLocaleDateString().slice(0, 10);
    } catch (e) {}

    return isEditing ? (
      <div id={`portaldate${id}`}>
        <APortal
          width={300}
          height={400}
          idParent={`portaldate${id}`}
          onClose={() => setIsEditing(false)}
        >
          <ObjFieldDate
            isEditing={isEditing}
            readOnly={!fieldDef.canWrite}
            handleChangeFieldValue={handleChangeDate}
            value={data}
            options={fieldDef.options}
            label={fieldDef.label}
            isStacked={isStacked}
          />
        </APortal>
      </div>
    ) : (
      <DivCellText
        $canWrite={fieldDef.canWrite}
        data-label={`${fieldDef.label}:`}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          if (fieldDef.canWrite) setIsEditing(true);
        }}
        id={id}
        $level={level}
        $isStacked={isStacked}
      >
        {value || ''}
      </DivCellText>
    );
  };

  const renderNumberCell = (data) => {
    res =
      fieldDef.canWrite && isEditing ? (
        <input
          type="number"
          defaultValue={data}
          onMouseUp={handlePreventInputEvent}
          onBlur={handleBlur}
          style={{
            width: '100%',
            height: '100%',
            fontSize: 12,
            maxWidth: '200px',
          }}
          autoFocus
        />
      ) : (
        <DataCells.DataCellNumber
          onClick={(e) => {
            if (fieldDef.canWrite) setIsEditing(true);
          }}
          value={isNaN(+data) ? '' : data}
          fg={fieldValue.ui.fg}
          bg={fieldValue.ui.bg}
          label={fieldDef.label}
          isStacked={isStacked}
          refValue={isNaN(fieldDef.refValue) ? 1000 : fieldDef.refValue}
          unit={fieldDef.unit ?? ''}
          $canWrite={fieldDef.canWrite}
        />
      );
    return res;
  };

  const IconOnly = ({ icon, color, title }) => (
    <div
      title={title}
      style={{
        width: 16,
        height: 16,
        backgroundColor: color || 'var(--ds-icon-base)',
        maskImage: `url('${icon || '/img/alert.svg'}')`,
        WebkitMaskImage: `url('${icon || '/img/alert.svg'}')`,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: '16px 16px',
        WebkitMaskSize: '16px 16px',
        opacity: 0.85,
        flex: '0 0 16px',
      }}
    />
  );

  const IconRow = ({ items = [], gap = 6 }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap,
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }}
    >
      {items.map((it, i) => (
        <IconOnly
          key={`${it.icon}-${i}`}
          icon={it.icon}
          color={it.color}
          title={it.title}
        />
      ))}
    </div>
  );

  const renderYesNo = (item) => {
    if (item.operation && item.operation === 'contains') {
      return (
        <DataCells.DataCellYesNo
          color={
            data && data.indexOf(item.value) > -1 ? '#7FC97F' : 'transparent'
          }
        />
      );
    }
  };
  const renderArea = () => {
    let dispval = data;
    let isName = item.data === 'name';

    let res =
      fieldDef.canWrite && isEditing ? (
        <div id={`portalArea${id}`}>
          <APortal
            idParent={`portalArea${id}`}
            onClose={() => setIsEditing(false)}
            width={600}
            height={400}
          >
            <TiptapEditor
              value={data}
              onUpdate={(newValue) => setValue(newValue)}
              editable={true}
            />
          </APortal>
        </div>
      ) : (
        <DivCellText
          $isName={isName}
          data-label={`${fieldDef.label}:`}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (fieldDef.canWrite) setIsEditing(true);
          }}
          id={id}
          $isStacked={isStacked}
        >
          {removeTagHtml(dispval) || ''}
        </DivCellText>
      );
    return res;
  };

  const renderText = () => {
    let isName = item.data === 'name';
    let res =
      fieldDef.canWrite && isEditing ? (
        <input
          onMouseUp={handlePreventInputEvent}
          type="text"
          defaultValue={data}
          onChange={(e) => setValue(e.target.value)}
          onBlur={changeValue}
          autoFocus
          style={{
            width: '100%',
            maxWidth: '800px',
            height: '100%',
            fontSize: 12,
          }}
        />
      ) : (
        <DivCellText
          data-label={`${fieldDef.label}:`}
          $canWrite={fieldDef.canWrite}
          $isName={isName}
          $isStacked={isStacked}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (fieldDef.canWrite) setIsEditing(true);
          }}
          id={id}
          $level={level}
          $fg={fieldValue.ui.fg}
          $bg={fieldValue.ui.bg}
        >
          {removeTagHtml(fieldValue.displayValue) || ''}
          {fieldDef.unit ?? ''}
        </DivCellText>
      );
    return res;
  };

  const renderList = (data, iconOnly = false) => {
    const option = singleFieldList.selected;
    let res = isEditing ? (
      <div id={`portalliste${id}`}>
        <APortal
          idParent={`portalliste${id}`}
          onClose={() => setIsEditing(false)}
        >
          <ObjFieldList
            isEditing={isEditing}
            readOnly={!fieldDef.canWrite}
            handleChangeFieldValue={handleChangeDate}
            value={data}
            options={fieldDef.options}
            unit={fieldDef.unit ?? ''}
            orientation="vertical"
          />
        </APortal>
      </div>
    ) : iconOnly ? (
      <IconRow
        items={[
          {
            icon: option?.icon,
            color: option?.color,
            title: option?.display,
          },
        ]}
      />
    ) : (
      <CellList
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          if (fieldDef.canWrite) setIsEditing(true);
        }}
        isStacked={isStacked}
        icon={option?.icon}
        value={option?.display}
        color={option?.color}
        label={fieldDef.label}
        unit={fieldDef.unit ?? ''}
        canWrite={fieldDef.canWrite}
      />
    );
    return res;
  };

  const renderListMulti = (valuesArray = []) => {
    if (isEditing) {
      return (
        <div id={`portalliste${id}`}>
          <APortal
            idParent={`portalliste${id}`}
            onClose={() => setIsEditing(false)}
          >
            <ObjFieldListMulti
              key={`listmulti-${id}`}
              isEditing={isEditing}
              readOnly={!fieldDef.canWrite}
              onChangeValues={(vals) => {
                // on pousse l'ensemble d'un coup (même clé que d’habitude)
                handleChangeDate(`${item.data}_multval`, vals);
              }}
              values={valuesArray}
              options={fieldDef.options}
              unit={fieldDef.unit ?? ''}
              isModal={true}
              orientation="vertical"
            />
          </APortal>
        </div>
      );
    }

    return (
      <CellListInlineGroup>
        {multiFieldList.selectedList.map((it, i) => (
          <CellListBadge
            key={`${it.title}-${i}`}
            $bg={it.color ? lighten(it.color, 0.9) : 'var(--ds-bg-secondary)'}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => {
              if (fieldDef.canWrite) setIsEditing(true);
            }}
          >
            <CellListBadgeIcon $color={it.color} $icon={it.icon} />
            <CellListBadgeText className="badge-text">
              {it.display}
              {fieldDef.unit ?? ''}
            </CellListBadgeText>
          </CellListBadge>
        ))}
      </CellListInlineGroup>
    );
  };

  const renderLinked = (data) => {
    let res = isEditing ? (
      <div id={`portalliste${id}`}>
        <APortal
          idParent={`portalliste${id}`}
          onClose={() => setIsEditing(false)}
        >
          <ObjectSelector
            typeSelectedObject={fieldDef.type}
            isDisplaySummary={false}
            isDisplayTitle={false}
            isDisplayConfirm={false}
            onSelectValue={(value) => {
              setValue(value);
              setIsEditing(false);
            }}
          />
        </APortal>
      </div>
    ) : (
      <DivCellText
        $canWrite={fieldDef.canWrite}
        $isStacked={isStacked}
        data-label={`${fieldDef.label}:`}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          if (fieldDef.canWrite) setIsEditing(true);
        }}
        id={id}
      >
        {removeTagHtml(fieldValue.displayValue) || ''}
      </DivCellText>
    );
    return res;
  };

  let res = null;

  if (item.data === 'crumbtrail') {
    res = data && (
      <DataCells.DataCellCrumbtrail
        value={data}
        crumbtrailIds={node.data.crumbtrailIds}
      ></DataCells.DataCellCrumbtrail>
    );
  } else {
    switch (item.type) {
      case 'yesno':
        res = renderYesNo(item);
        break;

      case 'date':
        res = renderDateCell(data, id);
        break;

      case 'number':
        res = renderNumberCell(data);
        break;

      case 'text':
        if (fieldDef.type === 'list') {
          if (fieldDef.isMultiple) {
            const raw = node?.data?.[`${fieldName}_multval`];
            const multVals = Array.isArray(raw)
              ? raw
              : typeof raw === 'string'
              ? parseJson(raw) || []
              : [];
            res = renderListMulti(multVals);
          } else {
            res = renderList(data);
          }
        } else if (fieldDef.options?.includes('richtext')) {
          res = renderArea();
        } else if (fieldDef.isLinked) {
          res = renderLinked(data);
        } else {
          res = renderText();
        }

        break;

      default:
        break;
    }
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        width: width || '100%',
        alignItems: 'center',
        gap: '6px',
        lineHeight: 1,
        border: '0px solid pink',
      }}
    >
      <div
        ref={cellInnerRef}
        style={{ width: 'calc(100% - 6px)', overflow: 'hidden' }}
      >
        {res}
      </div>
      {!isLastCell && (
        <DataColumnResizer
          panelNumber={panelNumber}
          colNumber={colNumber - 1}
          typeTo={typeTo}
          name={name}
        />
      )}
    </div>
  );
}
